import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import { useCreateOptionGroup } from "../../hooks/optionGroups/useCreateOptionGroup";
import { useUpdateOptionGroup } from "../../hooks/optionGroups/useUpdateOptionGroup";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditOptionGroup = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [code, setCode] = useState("");
    const [displayType, setDisplayType] = useState("dropdown");
    const [isActive, setIsActive] = useState(true);

    // Hooks
    const { createOptionGroupMutate, isCreating } = useCreateOptionGroup();
    const { editOptionGroup, isUpdating } = useUpdateOptionGroup();

    const loadingSubmit = isCreating || isUpdating;

    // Display Type Options - Format: { id, name } for SelectAutoComplacent
    const displayTypeOptions = [
        { id: "dropdown", name: { ar: "قائمة منسدلة", en: "Dropdown" } },
        { id: "radio", name: { ar: "أزرار اختيار", en: "Radio Buttons" } },
        { id: "color", name: { ar: "منتقي ألوان", en: "Color Picker" } },
        { id: "image", name: { ar: "صور", en: "Images" } },
    ];

    // Auto-generate code from English name (works in both create and edit modes)
    useEffect(() => {
        if (nameEn) {
            // Convert to lowercase and replace spaces with underscores
            const generatedCode = nameEn
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '_')  // Replace spaces with underscore
                .replace(/[^a-z0-9_]/g, '');  // Remove any non-alphanumeric except underscore
            setCode(generatedCode);
        }
    }, [nameEn]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            const data = Array.isArray(currentData) ? currentData[0] : currentData;
            if (data) {
                setNameAr(data?.name?.ar || "");
                setNameEn(data?.name?.en || "");
                setCode(data?.code || "");
                setDisplayType(data?.display_type || "dropdown");
                setIsActive(data?.is_active ?? true);
            }
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();

        const trimmedEn = nameEn.trim();
        const trimmedAr = nameAr.trim();

        const payload = {
            name: {
                en: trimmedEn,
                ar: trimmedAr || trimmedEn,
            },
            code: code,
            display_type: displayType,
            is_active: isActive,
        };

        if (isEdit && currentData?.id) {
            editOptionGroup(
                { id: currentData.id, data: payload },
                { onSuccess: (res) => { if (onSubmitSuccess) onSubmitSuccess(res); } }
            );
        } else {
            createOptionGroupMutate(payload, {
                onSuccess: (res) => { if (onSubmitSuccess) onSubmitSuccess(res); }
            });
        }
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit
                            ? (isArabic ? "تعديل مجموعة خيارات" : "Edit Option Group")
                            : (isArabic ? "إضافة مجموعة خيارات جديدة" : "Create New Option Group")}
                    </Typography>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Multilingual Names */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="الاسم (عربي)"
                                placeholder="مثل: اللون، المقاس، السعة"
                                type="text"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                helperText="اختياري — لو سيبتها فاضية هتتعبّى تلقائياً من الاسم الإنجليزي"
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Name (English)"
                                placeholder="e.g., Color, Size, Storage"
                                type="text"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {/* Code (Auto-generated from English name) */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الكود (يتم توليده تلقائياً)" : "Code (Auto-generated)"}
                            placeholder="e.g., color, size, storage"
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            required
                            disabled={true}  // Always disabled (auto-generated from English name)
                            helperText={isArabic
                                ? "يتم توليد الكود تلقائياً من الاسم الإنجليزي"
                                : "Auto-generated from English name"}
                        />
                    </Grid>

                    {/* Display Type */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "نوع العرض" : "Display Type"}
                            type="select"
                            name="display_type"
                            value={displayType}
                            onChange={(e) => setDisplayType(e.target.value)}
                            options={displayTypeOptions}
                            required
                        />
                    </Grid>

                    {/* Active Status */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "نشط" : "Active"}
                            type="switch"
                            name="is_active"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit
                            ? (isArabic ? "جارٍ الحفظ..." : "Saving...")
                            : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditOptionGroup;
